import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../src/firebaseConfig';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  setDoc,
  doc
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import styles from './AccountRegistration.css';
import backgroundImage from '../assets/background.jpg';

function AccountRegistration() {
  const [codigo, setCodigo] = useState('');
  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [plataforma, setPlataforma] = useState('');
  const [plataformas, setPlataformas] = useState([]); // Nueva estado para plataformas
  const [pais, setPais] = useState('');
  const [tarjeta, setTarjeta] = useState('');
  const [propietario, setPropietario] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaCorte, setFechaCorte] = useState('');
  const [pantallas, setpantallas] = useState('');

  // Perfiles y PINs (mismo índice)
  const [perfiles, setPerfiles] = useState([]);
  const [pines, setPines] = useState([]);

  const [isPaisNA, setIsPaisNA] = useState(false);
  const [isTarjetaNA, setIsTarjetaNA] = useState(false);
  const [isPropietarioNA, setIsPropietarioNA] = useState(false);
  const [isFechaInicioNA, setIsFechaInicioNA] = useState(false);
  const [isFechaCorteNA, setIsFechaCorteNA] = useState(false);

  const navigate = useNavigate();

  const backgroundStyle = {
    minHeight: '100vh',
    width: '100%',
    margin: 0,
    padding: 0,
    backgroundImage: `linear-gradient(
      rgba(0, 0, 0, 0.65),
      rgba(0, 0, 0, 0.75)
    ), url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
    overflowX: 'hidden'
  };

  // Nueva función para obtener plataformas desde Firebase
  const fetchPlataformas = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'plataformas'));
      const data = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      // Ordenar alfabéticamente por nombre
      data.sort((a, b) => a.nombre.localeCompare(b.nombre));
      setPlataformas(data);
    } catch (error) {
      console.error("Error al obtener plataformas:", error);
      // Fallback a plataformas estáticas en caso de error
      setPlataformas([
        { id: 'netflix', nombre: 'Netflix', imagen: '' },
        { id: 'max', nombre: 'MAX', imagen: '' },
        { id: 'disney', nombre: 'Disney plus', imagen: '' },
        { id: 'paramount', nombre: 'Paramount', imagen: '' },
        { id: 'prime', nombre: 'Prime Video', imagen: '' },
        { id: 'crunchyroll', nombre: 'Crunchyroll', imagen: '' },
        { id: 'star', nombre: 'Star plus', imagen: '' },
        { id: 'spotify', nombre: 'Spotify', imagen: '' },
        { id: 'canva', nombre: 'Canva Pro', imagen: '' },
        { id: 'apple', nombre: 'Apple TV', imagen: '' },
        { id: 'youtube', nombre: 'YouTube Premium', imagen: '' }
      ]);
    }
  }, []);

  // Cargar plataformas al montar el componente
  useEffect(() => {
    fetchPlataformas();
  }, [fetchPlataformas]);

  // --- helpers ---
  const toKey = (name) =>
    (name ?? '')
      .trim()
      .toLowerCase()
      // solo letras, números, guion y guion_bajo
      .replace(/[^\w-]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const handlePantallasChange = (e) => {
    const value = e.target.value;
    setpantallas(value);
    const n = Number(value) || 0;

    setPerfiles((prev) => {
      const next = [...prev];
      if (n > next.length) return [...next, ...Array(n - next.length).fill('')];
      return next.slice(0, n);
    });

    setPines((prev) => {
      const next = [...prev];
      if (n > next.length) return [...next, ...Array(n - next.length).fill('')];
      return next.slice(0, n);
    });
  };

  const handlePerfilChange = (index, value) => {
    setPerfiles((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handlePinChange = (index, value) => {
    setPines((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!codigo || !correo || !contrasena || !plataforma) {
      alert('Por favor, completa todos los campos obligatorios.');
      return;
    }

    const nPantallas = Number(pantallas);
    if (Number.isNaN(nPantallas) || nPantallas <= 0) {
      alert('Pantallas debe ser un número mayor a 0.');
      return;
    }

    if (perfiles.length !== nPantallas) {
      alert(`Debes ingresar ${nPantallas} perfiles.`);
      return;
    }
    if (pines.length !== nPantallas) {
      alert(`Debes ingresar ${nPantallas} PIN(es), uno por cada perfil.`);
      return;
    }

    // Normalización y validaciones
    const displayNames = perfiles.map((p) => (p ?? '').trim());
    const keys = displayNames.map(toKey);
    const pins = pines.map((p) => (p ?? '').trim());

    if (displayNames.some((p) => p.length === 0)) {
      alert('Todos los perfiles deben tener un nombre.');
      return;
    }
    if (pins.some((pin) => pin.length < 4)) {
      alert('Cada PIN debe tener al menos 4 caracteres.');
      return;
    }
    // Unicidad por nombre visual y por key normalizada
    const setDisplay = new Set(displayNames.map((p) => p.toLowerCase()));
    if (setDisplay.size !== displayNames.length) {
      alert('Los perfiles no pueden repetirse.');
      return;
    }
    const setKeys = new Set(keys);
    if (setKeys.size !== keys.length) {
      alert('Dos perfiles se normalizan al mismo id. Cambia sus nombres.');
      return;
    }
    if (keys.some((k) => k.length < 3)) {
      alert('Cada perfil debe generar un id válido (mín. 3 caracteres alfanuméricos).');
      return;
    }

    try {
      const accountsRef = collection(db, 'Cuentas');

      // Verificar si ya existe el código o el correo
      const codeQuery = query(accountsRef, where('codigo', '==', codigo));
      const emailQuery = query(accountsRef, where('correo', '==', correo));

      const [codeSnapshot, emailSnapshot] = await Promise.all([
        getDocs(codeQuery),
        getDocs(emailQuery)
      ]);

      if (!codeSnapshot.empty) {
        alert(`El código "${codigo}" ya está registrado.`);
        return;
      }

      if (!emailSnapshot.empty) {
        alert(`El correo "${correo}" ya está registrado.`);
        return;
      }

      // 1) Registrar la cuenta en "Cuentas" (como en tu versión)
      await addDoc(accountsRef, {
        codigo,
        correo,
        contrasena, // ⚠️ Ideal: no guardar contraseñas en Firestore; usar Firebase Auth
        plataforma,
        pantallas: nPantallas,
        pais: isPaisNA ? 'N/A' : pais,
        tarjeta: isTarjetaNA ? 'N/A' : tarjeta,
        propietario: isPropietarioNA ? 'N/A' : propietario,
        fechaInicio: isFechaInicioNA ? 'N/A' : fechaInicio,
        fechaCorte: isFechaCorteNA ? 'N/A' : fechaCorte
      });

      // 2) Crear documento "profiles" con ID aleatorio y código de cuenta
      const perfilesMap = {};
      keys.forEach((key, i) => {
        perfilesMap[key] = {
          displayName: displayNames[i],
          pin: pins[i],
          vendido: false
        };
      });

      await addDoc(collection(db, 'profiles'), {
        codigo: codigo,          // código de la cuenta como campo
        perfiles: perfilesMap,   // mapa { keyPerfil: { displayName, pin, vendido } }
        order: displayNames      // opcional: para mantener orden visual
      });

      alert('Cuenta y perfiles (con PIN) registrados exitosamente.');

      // Reset de estados
      setCodigo('');
      setCorreo('');
      setContrasena('');
      setPlataforma('');
      setPais('');
      setTarjeta('');
      setPropietario('');
      setFechaInicio('');
      setFechaCorte('');
      setpantallas('');
      setPerfiles([]);
      setPines([]);
      setIsPaisNA(false);
      setIsTarjetaNA(false);
      setIsPropietarioNA(false);
      setIsFechaInicioNA(false);
      setIsFechaCorteNA(false);
    } catch (error) {
      console.error('Error guardando la cuenta:', error);
      alert('Error al registrar la cuenta: ' + error.message);
    }
  };

  const handleCancel = () => {
    navigate('/home');
  };

  return (
    <div style={backgroundStyle}>
      <div className="account-registration-container">
        <h2>Registro de Cuentas</h2>
        <form onSubmit={handleSubmit} className="account-form">
          <div className="form-group">
            <label>Código *</label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Ingresa el código"
              required
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="Ingresa el correo"
              required
            />
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="text" // si quieres, cámbialo a "password"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              placeholder="Ingresa la contraseña"
              required
            />
          </div>

          <div className="formGroup">
            <label className="label">Plataforma *</label>
            {/* Select modificado para usar plataformas desde Firebase */}
            <select
              value={plataforma}
              onChange={(e) => setPlataforma(e.target.value)}
              className="select"
              required
            >
              <option value="">Selecciona un servicio</option>
              {plataformas.map(p => (
                <option key={p.id} value={p.nombre}>{p.nombre}</option>
              ))}
              <option value="Otro">Otro</option>
            </select>
          </div>

          <div className="form-group">
            <label>Pantallas *</label>
            <input
              type="number"
              value={pantallas}
              onChange={handlePantallasChange}
              placeholder="Número de pantallas"
              min="1"
              step="1"
              required
            />
          </div>

          {Number(pantallas) > 0 && (
            <div className="form-group">
              <label>Perfiles y PIN ({pantallas}) *</label>
              <div className="profiles-grid">
                {Array.from({ length: Number(pantallas) }).map((_, i) => (
                  <div key={i} className="profile-item" style={{ display: 'grid', gap: 8 }}>
                    <input
                      type="text"
                      value={perfiles[i] ?? ''}
                      onChange={(e) => handlePerfilChange(i, e.target.value)}
                      placeholder={`Nombre del perfil ${i + 1}`}
                      required
                    />
                    <input
                      type="text"
                      value={pines[i] ?? ''}
                      onChange={(e) => handlePinChange(i, e.target.value)}
                      placeholder={`PIN del perfil ${i + 1}`}
                      required
                    />
                  </div>
                ))}
              </div>
              <small>
                El nombre debe ser único; el PIN mínimo 4 caracteres.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>País</label>
            <input
              type="text"
              value={isPaisNA ? 'N/A' : pais}
              onChange={(e) => setPais(e.target.value)}
              disabled={isPaisNA}
              placeholder="Ingresa el país"
            />
            <label>
              <input
                type="checkbox"
                checked={isPaisNA}
                onChange={(e) => setIsPaisNA(e.target.checked)}
              />
              N/A
            </label>
          </div>

          <div className="form-group">
            <label>Tarjeta</label>
            <input
              type="text"
              value={isTarjetaNA ? 'N/A' : tarjeta}
              onChange={(e) => setTarjeta(e.target.value)}
              disabled={isTarjetaNA}
              placeholder="Ingresa la tarjeta"
            />
            <label>
              <input
                type="checkbox"
                checked={isTarjetaNA}
                onChange={(e) => setIsTarjetaNA(e.target.checked)}
              />
              N/A
            </label>
          </div>

          <div className="form-group">
            <label>Propietario</label>
            <input
              type="text"
              value={isPropietarioNA ? 'N/A' : propietario}
              onChange={(e) => setPropietario(e.target.value)}
              disabled={isPropietarioNA}
              placeholder="Ingresa el propietario"
            />
            <label>
              <input
                type="checkbox"
                checked={isPropietarioNA}
                onChange={(e) => setIsPropietarioNA(e.target.checked)}
              />
              N/A
            </label>
          </div>

          <div className="form-group">
            <label>Fecha de Inicio</label>
            <input
              type="date"
              value={isFechaInicioNA ? '' : fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              disabled={isFechaInicioNA}
            />
            <label>
              <input
                type="checkbox"
                checked={isFechaInicioNA}
                onChange={(e) => setIsFechaInicioNA(e.target.checked)}
              />
              N/A
            </label>
          </div>

          <div className="form-group">
            <label>Fecha de Corte</label>
            <input
              type="date"
              value={isFechaCorteNA ? '' : fechaCorte}
              onChange={(e) => setFechaCorte(e.target.value)}
              disabled={isFechaCorteNA}
            />
            <label>
              <input
                type="checkbox"
                checked={isFechaCorteNA}
                onChange={(e) => setIsFechaCorteNA(e.target.checked)}
              />
              N/A
            </label>
          </div>

          <div className="button-group">
            <button type="submit" className={styles?.searchButton || 'btn'}>
              Registrar Cuenta
            </button>
            <button type="button" className="btn cancel" onClick={handleCancel}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AccountRegistration;